/**
 * V57 + V58: 내 평가기록 페이지
 *
 * 2탭 구조:
 * - Tab1: 내 평가 (전직원) — 자가평가 대기/완료 등 내 평가 목록
 * - Tab2: 리드 평가 (role_tl/role_manager) — 내가 리드평가자인 평가 목록
 *
 * V58: 리드 평가 탭 활성화 — LeadEvaluationModal 추가
 *       self_submitted 상태 평가 클릭 → 멘토(리드) 평가 입력 모달
 */

import React, { useEffect, useState, useCallback } from 'react';
import { ModalOverlay } from '../../shared/components/ModalOverlay';
import {
  useMyEvaluations,
  EVALUATION_TYPE_LABELS,
  EVALUATION_STATUS_LABELS,
  FINAL_GRADE_LABELS,
  EvaluationItem,
  EvaluationSection,
  EvaluationStatus,
  EvaluationNode,
  EvaluationItemV2,
  ScoringScale,
  EvaluationTotals,
} from '../../shared/hooks/useEvaluations';
import { useAuthContext } from '../../shared/hooks/useAuthContext';
import { ProbationEvaluationForm, isProbationType } from '../../shared/components/ProbationEvaluationForm';

// ============================================
// 자가평가 모달
// ============================================

interface SelfEvaluationModalProps {
  evaluation: EvaluationItem;
  sections: EvaluationSection[];
  onClose: () => void;
  onSubmit: (data: Record<string, any>, comment: string) => Promise<void>;
  submitError?: string | null;
}

// V2 helpers
function getV2TopNodes(nodes: EvaluationNode[]): EvaluationNode[] {
  return nodes.filter(n => n.parentId === null && n.isActive).sort((a, b) => a.order - b.order);
}
function getV2ChildNodes(nodes: EvaluationNode[], parentId: string): EvaluationNode[] {
  return nodes.filter(n => n.parentId === parentId && n.isActive).sort((a, b) => a.order - b.order);
}
function getV2NodeItems(items: EvaluationItemV2[], nodeId: string, role: string): EvaluationItemV2[] {
  return items.filter(i => i.nodeId === nodeId && i.isActive && i.requiredFor.includes(role as any)).sort((a, b) => a.order - b.order);
}
function findV2TopAncestorId(nodeId: string, nodes: EvaluationNode[]): string {
  const map = new Map(nodes.map(n => [n.id, n]));
  let current = nodeId;
  for (let i = 0; i < 10; i++) {
    const node = map.get(current);
    if (!node || node.parentId === null) return current;
    current = node.parentId;
  }
  return current;
}

function V2ItemInput({ item, value, scale, onChange }: {
  item: EvaluationItemV2; value: any; scale: ScoringScale; onChange: (v: any) => void;
}) {
  if (item.inputType === 'text') {
    return (
      <textarea value={value || ''} onChange={e => onChange(e.target.value)} rows={2}
        style={{ width: '100%', padding: 8, border: '1px solid #ddd', borderRadius: 4, resize: 'vertical' }} />
    );
  }
  if (item.inputType === 'select') {
    return (
      <select value={value || ''} onChange={e => onChange(e.target.value)}
        style={{ width: '100%', padding: 8, border: '1px solid #ddd', borderRadius: 4 }}>
        <option value="">선택</option>
        {(item.options || []).map(opt => <option key={opt} value={opt}>{opt}</option>)}
      </select>
    );
  }
  // rating
  const buttons: number[] = [];
  for (let n = scale.min; n <= scale.max; n += scale.step) buttons.push(n);
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {buttons.map(n => (
        <button key={n} onClick={() => onChange(n)} style={{
          width: 36, height: 36, borderRadius: 4, border: '1px solid #ddd',
          background: value === n ? '#3B82F6' : 'white', color: value === n ? 'white' : '#333',
          cursor: 'pointer', fontWeight: 600,
        }}>{n}</button>
      ))}
    </div>
  );
}

function V2TotalsDisplay({ totals, label }: { totals: EvaluationTotals; label: string }) {
  const categories = Object.values(totals.byCategory);
  return (
    <div style={{ background: '#F0FDF4', padding: 12, borderRadius: 4, marginBottom: 12 }}>
      <strong>{label}</strong>
      {categories.map(cat => (
        <div key={cat.nodeId} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginTop: 4 }}>
          <span>{cat.label}</span>
          <span>{Number(cat.total).toFixed(2)} / {Number(cat.maxPossible).toFixed(2)}</span>
        </div>
      ))}
      <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 600, marginTop: 8, paddingTop: 4, borderTop: '1px solid #BBF7D0' }}>
        <span>합계</span>
        <span>{Number(totals.grandTotal).toFixed(2)} / {Number(totals.grandMaxPossible).toFixed(2)}</span>
      </div>
    </div>
  );
}

function SelfEvaluationModal({ evaluation, sections, onClose, onSubmit, submitError }: SelfEvaluationModalProps) {
  const isV2 = (evaluation.templateNodes?.length || 0) > 0;
  const isProbation = isProbationType(evaluation.templateType);
  const selfSections = sections.filter(s => s.requiredFor.includes('self'));

  const [formData, setFormData] = useState<Record<string, any>>(evaluation.selfEvaluation || {});
  const [comment, setComment] = useState(evaluation.selfComment || '');
  const [submitting, setSubmitting] = useState(false);

  const handleFieldChange = (id: string, value: any) => {
    setFormData(prev => ({ ...prev, [id]: value }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await onSubmit(formData, comment);
    } finally {
      setSubmitting(false);
    }
  };

  const renderV2Form = () => {
    const nodes = evaluation.templateNodes || [];
    const items = evaluation.templateItems || [];
    const scale = evaluation.templateScoringScale || { min: 1, max: 5, step: 1 };
    const topNodes = getV2TopNodes(nodes);

    const renderNodeItems = (nodeId: string, depth: number) => {
      const nodeItems = getV2NodeItems(items, nodeId, 'self');
      const children = getV2ChildNodes(nodes, nodeId);
      return (
        <>
          {nodeItems.map(item => (
            <div key={item.id} style={{ marginBottom: 12, paddingLeft: depth * 16 }}>
              <label style={{ fontWeight: 500, display: 'block', marginBottom: 4, fontSize: 14 }}>
                {item.title}
                {item.inputType === 'rating' && item.weight !== 1 && (
                  <span style={{ color: '#999', fontSize: 12, marginLeft: 6 }}>가중치 {item.weight}</span>
                )}
              </label>
              {item.description && <p style={{ color: '#666', fontSize: 12, margin: '0 0 4px' }}>{item.description}</p>}
              <V2ItemInput item={item} value={formData[item.id]} scale={scale} onChange={v => handleFieldChange(item.id, v)} />
            </div>
          ))}
          {children.map(child => (
            <div key={child.id}>
              <div style={{ fontWeight: 600, fontSize: 13, color: '#4B5563', marginTop: 8, marginBottom: 4, paddingLeft: depth * 16 }}>
                {child.title}
              </div>
              {renderNodeItems(child.id, depth + 1)}
            </div>
          ))}
        </>
      );
    };

    return topNodes.length === 0 ? (
      <p style={{ color: '#999' }}>자가평가 항목이 없습니다.</p>
    ) : (
      topNodes.map(node => (
        <div key={node.id} style={{ marginBottom: 16, padding: 12, background: '#F9FAFB', borderRadius: 6 }}>
          <h4 style={{ margin: '0 0 8px', fontSize: 15, color: '#1F2937' }}>{node.title}</h4>
          {renderNodeItems(node.id, 0)}
        </div>
      ))
    );
  };

  return (
    <ModalOverlay onClose={onClose} style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000,
    }}>
      <div style={{
        background: 'white', borderRadius: 8, padding: 24, width: '95%',
        maxWidth: 1100,
        maxHeight: '95vh',
        overflow: 'auto',
      }}>
        <h3 style={{ marginTop: 0 }}>자가평가 작성</h3>
        <p style={{ color: '#666', fontSize: 14 }}>
          {evaluation.templateTitle?.replace(/\s*\(시스템\)/, '')} — {evaluation.targetUserName}
        </p>

        {/* V57.8+V60.6: 수습사원 평가 → PDF 테이블 폼 (큰 모달) */}
        {isV2 && isProbation ? (
          <ProbationEvaluationForm
            mode="self"
            nodes={evaluation.templateNodes || []}
            items={evaluation.templateItems || []}
            scoringScale={evaluation.templateScoringScale || { min: 1, max: 4, step: 1 }}
            templateType={evaluation.templateType}
            formData={formData}
            onChange={handleFieldChange}
            attendanceSummary={evaluation.attendanceSummary}
          />
        ) : isV2 ? renderV2Form() : (
          selfSections.length === 0 ? (
            <p style={{ color: '#999' }}>자가평가 항목이 없습니다.</p>
          ) : (
            selfSections.map(section => (
              <div key={section.id} style={{ marginBottom: 16 }}>
                <label style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>
                  {section.title}
                </label>
                {section.description && (
                  <p style={{ color: '#666', fontSize: 13, margin: '0 0 8px' }}>{section.description}</p>
                )}
                {section.inputType === 'text' ? (
                  <textarea
                    value={formData[section.id] || ''}
                    onChange={e => handleFieldChange(section.id, e.target.value)}
                    rows={3}
                    style={{ width: '100%', padding: 8, border: '1px solid #ddd', borderRadius: 4, resize: 'vertical' }}
                  />
                ) : section.inputType === 'select' ? (
                  <select
                    value={formData[section.id] || ''}
                    onChange={e => handleFieldChange(section.id, e.target.value)}
                    style={{ width: '100%', padding: 8, border: '1px solid #ddd', borderRadius: 4 }}
                  >
                    <option value="">선택</option>
                    {(section.options || []).map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                ) : (
                  <div style={{ display: 'flex', gap: 8 }}>
                    {Array.from({ length: section.inputType === 'rating_5' ? 5 : 10 }, (_, i) => i + 1).map(n => (
                      <button
                        key={n}
                        onClick={() => handleFieldChange(section.id, n)}
                        style={{
                          width: 36, height: 36, borderRadius: 4, border: '1px solid #ddd',
                          background: formData[section.id] === n ? '#3B82F6' : 'white',
                          color: formData[section.id] === n ? 'white' : '#333',
                          cursor: 'pointer', fontWeight: 600,
                        }}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))
          )
        )}

        {/* 총평: probation은 ProbationEvaluationForm 안에서 처리하므로 별도 표시 불필요 */}
        {!isProbation && (
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>총평 (선택)</label>
            <textarea
              value={comment}
              onChange={e => setComment(e.target.value)}
              rows={3}
              placeholder="자가평가 총평을 입력하세요"
              style={{ width: '100%', padding: 8, border: '1px solid #ddd', borderRadius: 4, resize: 'vertical' }}
            />
          </div>
        )}

        {/* V57.3+: 검증 에러 표시 */}
        {submitError && (
          <div style={{ padding: 10, background: '#FEF2F2', color: '#DC2626', borderRadius: 6, marginBottom: 12, fontSize: 13, lineHeight: 1.5 }}>
            {submitError}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button
            onClick={onClose}
            style={{ padding: '8px 16px', border: '1px solid #ddd', borderRadius: 4, background: 'white', cursor: 'pointer' }}
          >
            취소
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            style={{
              padding: '8px 16px', border: 'none', borderRadius: 4,
              background: '#3B82F6', color: 'white', cursor: submitting ? 'not-allowed' : 'pointer',
            }}
          >
            {submitting ? '제출 중...' : '자가평가 제출'}
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}

// ============================================
// V58: 리드(멘토) 평가 모달
// ============================================

interface LeadEvaluationModalProps {
  evaluation: EvaluationItem;
  onClose: () => void;
  onSubmit: (data: Record<string, any>, comment: string) => Promise<void>;
  submitError?: string | null;
}

function LeadEvaluationModal({ evaluation, onClose, onSubmit, submitError }: LeadEvaluationModalProps) {
  const isV2 = (evaluation.templateNodes?.length || 0) > 0;
  const isProbation = isProbationType(evaluation.templateType);
  const [formData, setFormData] = useState<Record<string, any>>(evaluation.leadEvaluation || {});
  const [comment, setComment] = useState(evaluation.leadComment || '');
  const [submitting, setSubmitting] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const handleFieldChange = (id: string, value: any) => {
    setFormData(prev => ({ ...prev, [id]: value }));
  };

  const handleSubmit = async () => {
    // V62.2: 수습 평가 → 멘토 피드백 필수
    if (isProbation && !comment.trim()) {
      setValidationError('멘토 피드백을 입력해주세요.');
      return;
    }
    setValidationError(null);
    setSubmitting(true);
    try {
      await onSubmit(formData, comment);
    } finally {
      setSubmitting(false);
    }
  };

  // V2 일반 폼 (non-probation)
  const renderV2LeadForm = () => {
    const nodes = evaluation.templateNodes || [];
    const items = evaluation.templateItems || [];
    const scale = evaluation.templateScoringScale || { min: 1, max: 5, step: 1 };
    const topNodes = getV2TopNodes(nodes);

    const renderNodeItems = (nodeId: string, depth: number) => {
      const nodeItems = getV2NodeItems(items, nodeId, 'lead');
      const children = getV2ChildNodes(nodes, nodeId);
      return (
        <>
          {nodeItems.map(item => (
            <div key={item.id} style={{ marginBottom: 12, paddingLeft: depth * 16 }}>
              <label style={{ fontWeight: 500, display: 'block', marginBottom: 4, fontSize: 14 }}>
                {item.title}
                {item.inputType === 'rating' && item.weight !== 1 && (
                  <span style={{ color: '#999', fontSize: 12, marginLeft: 6 }}>가중치 {item.weight}</span>
                )}
              </label>
              {item.description && <p style={{ color: '#666', fontSize: 12, margin: '0 0 4px' }}>{item.description}</p>}
              <V2ItemInput item={item} value={formData[item.id]} scale={scale} onChange={v => handleFieldChange(item.id, v)} />
            </div>
          ))}
          {children.map(child => (
            <div key={child.id}>
              <div style={{ fontWeight: 600, fontSize: 13, color: '#4B5563', marginTop: 8, marginBottom: 4, paddingLeft: depth * 16 }}>
                {child.title}
              </div>
              {renderNodeItems(child.id, depth + 1)}
            </div>
          ))}
        </>
      );
    };

    const hasLeadItems = topNodes.some(node => {
      const nodeItems = getV2NodeItems(items, node.id, 'lead');
      return nodeItems.length > 0;
    });

    return !hasLeadItems ? (
      <p style={{ color: '#999' }}>리드평가 항목이 없습니다.</p>
    ) : (
      topNodes.map(node => {
        const nodeItems = getV2NodeItems(items, node.id, 'lead');
        const children = getV2ChildNodes(nodes, node.id);
        if (nodeItems.length === 0 && children.length === 0) return null;
        return (
          <div key={node.id} style={{ marginBottom: 16, padding: 12, background: '#F9FAFB', borderRadius: 6 }}>
            <h4 style={{ margin: '0 0 8px', fontSize: 15, color: '#1F2937' }}>{node.title}</h4>
            {renderNodeItems(node.id, 0)}
          </div>
        );
      })
    );
  };

  return (
    <ModalOverlay onClose={onClose} style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000,
    }}>
      <div style={{
        background: 'white', borderRadius: 8, padding: 24, width: '90%', maxWidth: 900, maxHeight: '90vh',
        overflow: 'auto',
      }}>
        <h3 style={{ marginTop: 0 }}>리드(멘토) 평가 작성</h3>
        <p style={{ color: '#666', fontSize: 14 }}>
          {evaluation.templateTitle?.replace(/\s*\(시스템\)/, '')} — {evaluation.targetUserName}
        </p>

        {/* V58: 수습사원 평가 → PPT 테이블 폼 (lead 모드) */}
        {isV2 && isProbationType(evaluation.templateType) ? (
          <ProbationEvaluationForm
            mode="lead"
            nodes={evaluation.templateNodes || []}
            items={evaluation.templateItems || []}
            scoringScale={evaluation.templateScoringScale || { min: 1, max: 4, step: 1 }}
            templateType={evaluation.templateType}
            selfEvaluation={evaluation.selfEvaluation || undefined}
            formData={formData}
            onChange={handleFieldChange}
            attendanceSummary={evaluation.attendanceSummary}
          />
        ) : isV2 ? renderV2LeadForm() : (
          <p style={{ color: '#999' }}>V1 템플릿은 리드평가를 지원하지 않습니다.</p>
        )}

        <div style={{ marginBottom: 16 }}>
          <label style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>
            멘토 피드백{isProbation ? ' *' : ' (선택)'}
          </label>
          <textarea
            value={comment}
            onChange={e => { setComment(e.target.value); setValidationError(null); }}
            rows={3}
            placeholder={isProbation ? '멘토 피드백을 입력하세요 (필수)' : '리드평가 총평을 입력하세요'}
            style={{
              width: '100%', padding: 8, borderRadius: 4, resize: 'vertical',
              border: validationError ? '2px solid #DC2626' : '1px solid #ddd',
            }}
          />
        </div>

        {(submitError || validationError) && (
          <div style={{ padding: 10, background: '#FEF2F2', color: '#DC2626', borderRadius: 6, marginBottom: 12, fontSize: 13, lineHeight: 1.5 }}>
            {validationError || submitError}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button
            onClick={onClose}
            style={{ padding: '8px 16px', border: '1px solid #ddd', borderRadius: 4, background: 'white', cursor: 'pointer' }}
          >
            취소
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            style={{
              padding: '8px 16px', border: 'none', borderRadius: 4,
              background: '#10B981', color: 'white', cursor: submitting ? 'not-allowed' : 'pointer',
            }}
          >
            {submitting ? '제출 중...' : '리드평가 제출'}
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}

// ============================================
// 평가 상세 모달 (읽기 전용)
// ============================================

interface EvaluationDetailModalProps {
  evaluation: EvaluationItem;
  onClose: () => void;
  hideFinalGrade?: boolean; // V280: 평가 대상자 본인에게 최종등급 숨김
  hideManagerComment?: boolean; // V306: 멘토에게 팀장 총평 숨김
}

function EvaluationDetailModal({ evaluation, onClose, hideFinalGrade, hideManagerComment }: EvaluationDetailModalProps) {
  const isProbation = isProbationType(evaluation.templateType);
  return (
    <ModalOverlay onClose={onClose} style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000,
    }}>
      <div style={{
        background: 'white', borderRadius: 8, padding: 24, width: '90%',
        maxWidth: 900,
        maxHeight: '90vh',
        overflow: 'auto',
      }}>
        <h3 style={{ marginTop: 0 }}>{evaluation.templateTitle?.replace(/\s*\(시스템\)/, '')}</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
          <div><span style={{ color: '#666' }}>평가 유형:</span> {EVALUATION_TYPE_LABELS[evaluation.templateType]}</div>
          <div><span style={{ color: '#666' }}>상태:</span> {EVALUATION_STATUS_LABELS[evaluation.status]}</div>
          {/* V66: 멘토 → 평가자 순서. mentorName 없으면 미표기. */}
          {evaluation.leadEvaluatorName && (
            <div><span style={{ color: '#666' }}>멘토:</span> {evaluation.leadEvaluatorName}</div>
          )}
          <div><span style={{ color: '#666' }}>평가자:</span> {evaluation.evaluatorName}</div>
          {!hideFinalGrade && evaluation.finalGrade && (
            <div><span style={{ color: '#666' }}>최종등급:</span> {FINAL_GRADE_LABELS[evaluation.finalGrade]}</div>
          )}
          {evaluation.evaluationPeriod && (
            <div><span style={{ color: '#666' }}>평가기간:</span> {evaluation.evaluationPeriod}</div>
          )}
        </div>

        {/* V57.8: 수습사원 평가 → PPT 테이블 폼 (readonly) */}
        {isProbationType(evaluation.templateType) && (evaluation.templateNodes?.length || 0) > 0 ? (
          <ProbationEvaluationForm
            mode="readonly"
            nodes={evaluation.templateNodes || []}
            items={evaluation.templateItems || []}
            scoringScale={evaluation.templateScoringScale || { min: 1, max: 4, step: 1 }}
            templateType={evaluation.templateType}
            selfEvaluation={evaluation.selfEvaluation || undefined}
            leadEvaluation={evaluation.leadEvaluation || undefined}
            managerEvaluation={hideManagerComment ? undefined : (evaluation.managerEvaluation || undefined)}
            formData={{}}
            selfTotals={evaluation.selfTotals}
            managerTotals={hideManagerComment ? undefined : evaluation.managerTotals}
            attendanceSummary={evaluation.attendanceSummary}
            hideManagerSection={hideManagerComment}
          />
        ) : (
          <>
            {/* V2 합산 결과 */}
            {evaluation.selfTotals && <V2TotalsDisplay totals={evaluation.selfTotals} label="자가평가 점수" />}
            {evaluation.leadTotals && <V2TotalsDisplay totals={evaluation.leadTotals} label="리드평가 점수" />}
            {!hideManagerComment && evaluation.managerTotals && <V2TotalsDisplay totals={evaluation.managerTotals} label="팀장평가 점수" />}
          </>
        )}

        {evaluation.selfComment && (
          <div style={{ marginBottom: 12 }}>
            <strong>자가평가 총평:</strong>
            <p style={{ background: '#F9FAFB', padding: 12, borderRadius: 4, margin: '4px 0' }}>{evaluation.selfComment}</p>
          </div>
        )}
        {/* V66: 3차 평가(probation_3m)에서는 "멘토 피드백/총평" 미노출.
            "멘토 종합의견"(폼 내 _i11)은 ProbationEvaluationForm 안에서 그대로 렌더되므로 영향 없음. */}
        {evaluation.leadComment && evaluation.templateType !== 'probation_3m' && (
          <div style={{ marginBottom: 12 }}>
            <strong>멘토 피드백:</strong>
            <p style={{ background: '#F9FAFB', padding: 12, borderRadius: 4, margin: '4px 0' }}>{evaluation.leadComment}</p>
          </div>
        )}
        {!hideManagerComment && evaluation.managerComment && (
          <div style={{ marginBottom: 12 }}>
            <strong>팀장 총평:</strong>
            <p style={{ background: '#F9FAFB', padding: 12, borderRadius: 4, margin: '4px 0' }}>{evaluation.managerComment}</p>
          </div>
        )}
        {evaluation.finalComment && (
          <div style={{ marginBottom: 12 }}>
            <strong>최종 코멘트:</strong>
            <p style={{ background: '#F9FAFB', padding: 12, borderRadius: 4, margin: '4px 0' }}>{evaluation.finalComment}</p>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{ padding: '8px 16px', border: '1px solid #ddd', borderRadius: 4, background: 'white', cursor: 'pointer' }}
          >
            닫기
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}

// ============================================
// MyEvaluationsPage
// ============================================

export function MyEvaluationsPage() {
  const authContext = useAuthContext();
  const {
    evaluations,
    loading,
    error,
    listMyEvaluations,
    getEvaluationDetail,
    submitSelfEvaluation,
    submitLeadEvaluation,
  } = useMyEvaluations();

  const [activeTab, setActiveTab] = useState<'my' | 'lead'>('my');
  const [yearFilter, setYearFilter] = useState(new Date().getFullYear());
  const [selectedEval, setSelectedEval] = useState<EvaluationItem | null>(null);
  const [modalMode, setModalMode] = useState<'self' | 'lead' | 'detail' | null>(null);

  // 내 평가 목록
  // V60: 근무평가(dispatch/contract/dispatch_contract)는 평가 대상자 본인에게 비공개
  const WORK_EVAL_TYPES = new Set(['dispatch', 'contract', 'dispatch_contract']);
  const myEvaluations = evaluations.filter(e =>
    e.targetUserId === authContext.userId && !WORK_EVAL_TYPES.has(e.templateType)
  );
  // 리드 평가 목록 (leadEvaluatorId === me)
  const leadEvaluations = evaluations.filter(e => e.leadEvaluatorId === authContext.userId && e.targetUserId !== authContext.userId);

  useEffect(() => {
    listMyEvaluations({ year: yearFilter });
  }, [yearFilter, listMyEvaluations]);

  // V63.9: 리드평가가 없으면 "내 평가" 탭으로 자동 복귀
  useEffect(() => {
    if (activeTab === 'lead' && leadEvaluations.length === 0 && !loading) {
      setActiveTab('my');
    }
  }, [activeTab, leadEvaluations.length, loading]);

  const handleRowClick = useCallback(async (evalItem: EvaluationItem) => {
    const detail = await getEvaluationDetail(evalItem.id);
    if (detail) {
      setSelectedEval(detail);
      // V58: 리드 평가 탭에서 self_submitted 상태 → 멘토 평가 입력
      if (activeTab === 'lead' && detail.status === 'self_submitted' && detail.leadEvaluatorId === authContext.userId) {
        setModalMode('lead');
      } else if (detail.status === 'draft' && detail.targetUserId === authContext.userId) {
        setModalMode('self');
      } else {
        setModalMode('detail');
      }
    }
  }, [getEvaluationDetail, authContext.userId, activeTab]);

  const handleSelfSubmit = useCallback(async (data: Record<string, any>, comment: string) => {
    if (!selectedEval) return;
    const success = await submitSelfEvaluation({
      evaluationId: selectedEval.id,
      selfEvaluation: data,
      selfComment: comment || undefined,
    });
    if (success) {
      setModalMode(null);
      setSelectedEval(null);
      listMyEvaluations({ year: yearFilter });
    }
  }, [selectedEval, submitSelfEvaluation, listMyEvaluations, yearFilter]);

  // V58: 리드평가 제출
  const handleLeadSubmit = useCallback(async (data: Record<string, any>, comment: string) => {
    if (!selectedEval) return;
    const success = await submitLeadEvaluation({
      evaluationId: selectedEval.id,
      leadEvaluation: data,
      leadComment: comment || undefined,
    });
    if (success) {
      setModalMode(null);
      setSelectedEval(null);
      listMyEvaluations({ year: yearFilter });
    }
  }, [selectedEval, submitLeadEvaluation, listMyEvaluations, yearFilter]);

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - i);

  // V63.8: 수습사원 본인 화면에서 최종등급 칼럼 숨김
  // V60: 평가 대상자 본인 화면에서 상태 칼럼 숨김
  const renderTable = (items: EvaluationItem[], hideFinalGrade = false, hideStatus = false) => (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr style={{ borderBottom: '2px solid #E5E7EB' }}>
          <th style={{ padding: '12px 8px', textAlign: 'left', fontSize: 14 }}>평가 유형</th>
          <th style={{ padding: '12px 8px', textAlign: 'left', fontSize: 14 }}>대상자</th>
          {!hideStatus && (
            <th style={{ padding: '12px 8px', textAlign: 'left', fontSize: 14 }}>상태</th>
          )}
          {!hideFinalGrade && (
            <th style={{ padding: '12px 8px', textAlign: 'left', fontSize: 14 }}>최종등급</th>
          )}
          <th style={{ padding: '12px 8px', textAlign: 'left', fontSize: 14 }}>평가기간</th>
        </tr>
      </thead>
      <tbody>
        {items.length === 0 ? (
          <tr>
            <td colSpan={5 - (hideFinalGrade ? 1 : 0) - (hideStatus ? 1 : 0)} style={{ padding: 24, textAlign: 'center', color: '#999' }}>
              평가 데이터가 없습니다.
            </td>
          </tr>
        ) : (
          items.map(evalItem => (
            <tr
              key={evalItem.id}
              onClick={() => handleRowClick(evalItem)}
              style={{ borderBottom: '1px solid #F3F4F6', cursor: 'pointer' }}
              onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#F9FAFB')}
              onMouseLeave={e => (e.currentTarget.style.backgroundColor = '')}
            >
              <td style={{ padding: '12px 8px', fontSize: 14 }}>
                {EVALUATION_TYPE_LABELS[evalItem.templateType]}
              </td>
              <td style={{ padding: '12px 8px', fontSize: 14 }}>
                {evalItem.targetUserName}
                <span style={{ color: '#999', marginLeft: 4, fontSize: 12 }}>
                  {evalItem.targetUserOrgUnitName}
                </span>
              </td>
              {!hideStatus && (
                <td style={{ padding: '12px 8px', fontSize: 14 }}>
                  <span style={{
                    display: 'inline-block', padding: '2px 8px', borderRadius: 12, fontSize: 12,
                    background:
                      evalItem.status === 'draft' ? '#FEF3C7' :
                      evalItem.status === 'sent_to_hr' ? '#D1FAE5' :
                      evalItem.status === 'manager_submitted' ? '#DBEAFE' : '#F3F4F6',
                    color:
                      evalItem.status === 'draft' ? '#92400E' :
                      evalItem.status === 'sent_to_hr' ? '#065F46' :
                      evalItem.status === 'manager_submitted' ? '#1E40AF' : '#374151',
                  }}>
                    {EVALUATION_STATUS_LABELS[evalItem.status]}
                  </span>
                </td>
              )}
              {!hideFinalGrade && (
                <td style={{ padding: '12px 8px', fontSize: 14, fontWeight: evalItem.finalGrade ? 600 : 400 }}>
                  {evalItem.finalGrade || '-'}
                </td>
              )}
              <td style={{ padding: '12px 8px', fontSize: 14, color: '#666' }}>
                {evalItem.evaluationPeriod || evalItem.evaluationYear}
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  );

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px 16px' }}>
      <h2 style={{ margin: '0 0 24px', fontSize: 20 }}>평가 기록</h2>

      {/* 탭 — V63.9: 리드평가 탭은 leadEvaluations가 있을 때만 노출 */}
      {leadEvaluations.length > 0 && (
        <div style={{ display: 'flex', gap: 0, marginBottom: 16, borderBottom: '1px solid #E5E7EB' }}>
          <button
            onClick={() => setActiveTab('my')}
            style={{
              padding: '8px 16px', border: 'none', borderBottom: activeTab === 'my' ? '2px solid #3B82F6' : '2px solid transparent',
              background: 'none', cursor: 'pointer', fontWeight: activeTab === 'my' ? 600 : 400,
              color: activeTab === 'my' ? '#3B82F6' : '#666',
            }}
          >
            내 평가
          </button>
          <button
            onClick={() => setActiveTab('lead')}
            style={{
              padding: '8px 16px', border: 'none', borderBottom: activeTab === 'lead' ? '2px solid #3B82F6' : '2px solid transparent',
              background: 'none', cursor: 'pointer', fontWeight: activeTab === 'lead' ? 600 : 400,
              color: activeTab === 'lead' ? '#3B82F6' : '#666',
            }}
          >
            멘토 평가
          </button>
        </div>
      )}

      {/* 필터 */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
        <select
          value={yearFilter}
          onChange={e => setYearFilter(Number(e.target.value))}
          style={{ padding: '6px 12px', border: '1px solid #ddd', borderRadius: 4 }}
        >
          {yearOptions.map(y => (
            <option key={y} value={y}>{y}년</option>
          ))}
        </select>
      </div>

      {error && !modalMode && (
        <div style={{ padding: 12, background: '#FEF2F2', color: '#DC2626', borderRadius: 4, marginBottom: 16, fontSize: 14 }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>로딩 중...</div>
      ) : (
        renderTable(
          activeTab === 'my' ? myEvaluations : leadEvaluations,
          // V280: 평가 대상자 본인 화면에서 최종등급 칼럼 항상 숨김
          activeTab === 'my',
          // V60: 평가 대상자 본인 화면에서 상태 칼럼 숨김
          activeTab === 'my'
        )
      )}

      {/* 자가평가 모달 */}
      {modalMode === 'self' && selectedEval && (
        <SelfEvaluationModal
          evaluation={selectedEval}
          sections={selectedEval.templateSections || []}
          onClose={() => { setModalMode(null); setSelectedEval(null); }}
          onSubmit={handleSelfSubmit}
          submitError={error}
        />
      )}

      {/* V58: 리드평가 모달 */}
      {modalMode === 'lead' && selectedEval && (
        <LeadEvaluationModal
          evaluation={selectedEval}
          onClose={() => { setModalMode(null); setSelectedEval(null); }}
          onSubmit={handleLeadSubmit}
          submitError={error}
        />
      )}

      {/* 상세 모달 */}
      {modalMode === 'detail' && selectedEval && (
        <EvaluationDetailModal
          evaluation={selectedEval}
          onClose={() => { setModalMode(null); setSelectedEval(null); }}
          hideFinalGrade={activeTab === 'my'}
          hideManagerComment={activeTab === 'lead'}
        />
      )}
    </div>
  );
}
